import * as React from 'react';
import Image from 'next/image';
import { AssetSyncName } from 'components/badoo/asset/asset-sync';
import Icon from 'components/badoo/icon/icon';
import { P2 } from 'components/badoo/typography/typography';
import PageFooterBlock from 'components/badoo/page/page-footer-block';
import footer from 'components/badoo/lexemes/footer';
import { ExternalLink } from 'lib/app/external-link';
import { SupportedLanguage } from 'lib/lexemes/supported-languages';
import { getTwitterAccountByLanguage } from 'lib/seo/seo';
import { basePath } from 'lib/base-path';

type Props = {
    onBlogClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onCareersClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onSafetyCentreClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onHelpCentreClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onCitiesClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onChatClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onTermsClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onPrivacyClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onCookiePolicyClick?: React.MouseEventHandler<HTMLAnchorElement>;
    // onSitemapClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onModernSlaveryActClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onFacebookClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onLinkedInClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onInstagramClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onTwitterClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onAppleStoreClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onGoogleStoreClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onHuaweiAppGalleryClick?: React.MouseEventHandler<HTMLAnchorElement>;
    currentLanguage: SupportedLanguage;
};

const PageFooter: React.FC<Props> = ({
    onBlogClick,
    onCareersClick,
    onSafetyCentreClick,
    onHelpCentreClick,
    onCitiesClick,
    onChatClick,
    onTermsClick,
    onPrivacyClick,
    onCookiePolicyClick,
    // onSitemapClick,
    onModernSlaveryActClick,
    onFacebookClick,
    onLinkedInClick,
    onInstagramClick,
    onTwitterClick,
    onAppleStoreClick,
    onGoogleStoreClick,
    onHuaweiAppGalleryClick,
    currentLanguage,
}) => {
    const overviewLinks = [
        {
            title: footer.lxBlog,
            url: 'https://badoo.com/the-blog',
            onClick: onBlogClick,
        },
        {
            title: footer.lxSafetyCentre,
            url: ExternalLink.SAFETY_CENTRE,
            onClick: onSafetyCentreClick,
        },
        {
            title: footer.lxHelpCentre,
            url: 'https://badoo.com/help/',
            onClick: onHelpCentreClick,
        },
        {
            title: footer.lxChat,
            url: 'https://badoo.com/chat/',
            onClick: onCitiesClick,
        },
        {
            title: footer.lxCities,
            url: 'https://badoo.com/dating/',
            onClick: onChatClick,
        },
        { title: footer.lxTerms, url: ExternalLink.TERMS, onClick: onTermsClick },
        {
            title: footer.lxPrivacy,
            url: ExternalLink.PRIVACY,
            onClick: onPrivacyClick,
        },
        {
            title: footer.lxCookies,
            url: 'https://badoo.com/cookie-policy/',
            onClick: onCookiePolicyClick,
        },
        {
            title: footer.lxSlaveryAct,
            url: 'https://bumbcdn.com/big/documents/bumble/2023/statement-on-modern-slavery-act.pdf',
            onClick: onModernSlaveryActClick,
        },
    ];

    const socialLinks = [
        {
            title: footer.lxFacebook,
            icon: 'icon-facebook',
            url: 'https://www.facebook.com/badoo/',
            onClick: onFacebookClick,
        },
        {
            title: footer.lxLinkedIn,
            icon: 'icon-linkedin',
            url: 'https://www.linkedin.com/company/badoo/',
            onClick: onLinkedInClick,
        },
        {
            title: footer.lxInstagram,
            icon: 'icon-instagram',
            url: 'https://www.instagram.com/badoo/',
            onClick: onInstagramClick,
        },
        {
            title: footer.lxTwitter,
            icon: 'icon-twitter',
            url: `https://twitter.com/${getTwitterAccountByLanguage(
                currentLanguage
            )}?ref_src=twsrc%5Egoogle%7Ctwcamp%5Eserp%7Ctwgr%5Eauthor`,
            onClick: onTwitterClick,
        },
    ];

    const companyLinks = [
        {
            title: footer.lxCareers,
            url: 'https://team.bumble.com',
            onClick: onCareersClick,
        },
    ];

    return (
        <footer id="footer" className="page-footer">
            <div className="page-footer__inner">
                <div className="page-footer__blocks">
                    <PageFooterBlock id="overview" title={footer.lxOverview()}>
                        <ul className="page-column__links">
                            {overviewLinks.map((link, index) => (
                                <li className="page-column__links-item" key={index}>
                                    <a
                                        className="page-column__link"
                                        href={link.url}
                                        onClick={link.onClick}
                                    >
                                        <P2>{link.title()}</P2>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </PageFooterBlock>

                    <PageFooterBlock title={footer.lxPartOf()} id="company">
                        <ul className="page-column__links">
                            {companyLinks.map((link, index) => (
                                <li className="page-column__links-item" key={index}>
                                    <a
                                        className="page-column__link"
                                        href={link.url}
                                        onClick={link.onClick}
                                    >
                                        {link.title()}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </PageFooterBlock>

                    <PageFooterBlock title={footer.lxFollowUsTitle()} id="social">
                        <ul className="page-footer__social-links">
                            {socialLinks.map((link, index) => (
                                <li key={index}>
                                    <a
                                        className="page-footer__social-link"
                                        href={link.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={link.onClick}
                                    >
                                        <div className="page-footer__social-link-image">
                                            <Icon
                                                size="stretch"
                                                color="white"
                                                name={`${link.icon as AssetSyncName}`}
                                            />
                                        </div>
                                        <span className="page-footer__social-link-title">
                                            {link.title()}
                                        </span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </PageFooterBlock>

                    <PageFooterBlock title={footer.lxDownloadTitle()} id="mobile">
                        <div className="page-footer__app-block">
                            <div className="page-footer__app-badge">
                                <Icon size="stretch" name="app-badge" />
                            </div>

                            <div className="page-footer__app-stores-list">
                                <a
                                    className={`page-footer__app-store-badge page-footer__app-store-badge--app-store-${currentLanguage}`}
                                    aria-label="App Store"
                                    href={ExternalLink.APP_STORE_IOS}
                                    onClick={onAppleStoreClick}
                                >
                                    <Image
                                        src={`${basePath}l-assets/${currentLanguage}/appstore-badge.svg`}
                                        unoptimized={true}
                                        width={120}
                                        height={40}
                                        alt="App Store"
                                    />
                                </a>

                                <a
                                    className={`page-footer__app-store-badge page-footer__app-store-badge--google-play-${currentLanguage}`}
                                    aria-label="Google Play"
                                    href={ExternalLink.APP_STORE_ANDROID}
                                    onClick={onGoogleStoreClick}
                                >
                                    <Image
                                        src={`${basePath}l-assets/${currentLanguage}/google-play-badge.png`}
                                        width={646}
                                        height={250}
                                        alt="Google Play"
                                    />
                                </a>

                                <a
                                    className="page-footer__app-store-badge"
                                    aria-label="AppGallery"
                                    href={ExternalLink.APP_STORE_HUAWEI}
                                    onClick={onHuaweiAppGalleryClick}
                                >
                                    <Image
                                        src={`${basePath}l-assets/${currentLanguage}/appgallery-badge.png`}
                                        width={851}
                                        height={256}
                                        alt="HUAWEI AppGallery"
                                    />
                                </a>

                                <p
                                    className="page-footer__app-store-copy"
                                    dangerouslySetInnerHTML={{
                                        __html: footer.lxDownloadCopyText(),
                                    }}
                                />
                            </div>
                        </div>
                    </PageFooterBlock>
                </div>
            </div>

            <div className="page-footer__inner">
                <p className="page-footer__copy">{footer.lxCopyright()}</p>
            </div>
        </footer>
    );
};

export default PageFooter;
